import {
  AttachMoneyIcon,
  ChartLineIcon,
  FileBlankIcon,
  NavigatorItem,
  NavigatorLinkMenu,
  UserIcon,
  WarningCircleFilledIcon,
} from '@forgeglobal/design-framework';
import NotificationZone from '@tcs/client-portal-common/components/molecules/NotificationZone';
import { OnlineAccessSelector } from '@tcs/client-portal-common/components/molecules/OnlineAccessSelector';
import AppLayout from '@tcs/client-portal-common/components/organisms/ApplicationLayout';
import { useGetQuestion } from '@tcs/client-portal-common/hooks/auth';
import { useEsignConsentStatus } from '@tcs/client-portal-common/hooks/useEsignConsentStatus';
import useFeature, {
  FeatureFlag,
} from '@tcs/client-portal-common/hooks/useFeature';
import useNotifications from '@tcs/client-portal-common/hooks/useNotifications';
import useSession from '@tcs/client-portal-common/hooks/useSession';
import Account from '@tcs/client-portal-individual/pages/Account';
import Accounts from '@tcs/client-portal-individual/pages/Accounts';
import Documents from '@tcs/client-portal-individual/pages/Documents';
import Profile from '@tcs/client-portal-individual/pages/Profile';
import { getConfig } from '@tcs/client-portal/config/configService';
import { useAnalytics } from '@tcs/common/hooks/analytics';
import Funding from '@tcs/fund-account/Funding';
import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import FeesRedirect from './pages/Account/FeesRedirect';

const getDefaultNavigationItems: () => NavigatorItem[] = () => [
  {
    id: 'Accounts',
    icon: <ChartLineIcon />,
    to: '/accounts',
    exact: false,
  },
  {
    id: 'Documents',
    icon: <FileBlankIcon />,
    to: '/documents',
    exact: false,
  },
  {
    id: 'Profile',
    icon: <UserIcon />,
    to: '/profile',
    exact: false,
  },
  {
    id: 'Fund My Account',
    icon: <AttachMoneyIcon />,
    to: '/funding',
    exact: false,
  },
];

const NAVIGATION_LINK_MENU: NavigatorLinkMenu = {
  title: 'I WANT TO...',
  navigatorLinkItems: [
    {
      id: 'Open a New Account',
      to: 'https://forgetrust.com/forms/open-a-new-account',
    },
    {
      id: 'Make an Investment',
      to: 'https://forgetrust.com/forms/make-an-investment',
    },
    {
      id: 'Manage My Account',
      to: 'https://forgetrust.com/forms/manage-your-account',
    },
    {
      id: 'Disbursements and Distributions',
      to: 'https://forgetrust.com/forms/disbursements-and-distributions',
    },
    {
      id: 'Pay Account Fees',
      to: '/fees',
      navigatorExtLinkItem: {
        linkMessage:
          'You will be taken to a 3rd party payment portal in a separate tab. Would you like to continue?',
        modalTitle: 'Redirecting to the Payment Portal',
      },
    },
  ],
};

const IndividualModals = () => {
  const {
    claims: { clientPortalIdentity },
  } = useSession();

  const { data: esignConsentModel, loading } =
    useEsignConsentStatus(clientPortalIdentity);

  useNotifications();

  if (loading) {
    return null;
  }

  return (
    esignConsentModel?.esignConsentStatus === 'Unset' && (
      <OnlineAccessSelector accountId={clientPortalIdentity} />
    )
  );
};

const Layout = ({ navigationItems, clientPortalIdentity }) => {
  return (
    <AppLayout
      navigatorItems={navigationItems}
      navigatorLinkMenu={NAVIGATION_LINK_MENU}>
      {clientPortalIdentity && <NotificationZone />}
      <Outlet />
      <IndividualModals />
    </AppLayout>
  );
};

const Individual = () => {
  const [question, setQuestion] = useState('');
  const {
    claims: { clientPortalIdentity },
  } = useSession();

  const { trackPageView } = useAnalytics();
  const location = useLocation();

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location, trackPageView]);

  const { data: securityQuestion, loading } = useGetQuestion();

  const navigationItems = getDefaultNavigationItems();

  // set a warning icon on Profile item if security question is missing
  if (
    !loading &&
    (!securityQuestion || !securityQuestion.text) &&
    question === ''
  ) {
    navigationItems[2].secondaryIcon = (
      <WarningCircleFilledIcon fontSize="small" color="warning" />
    );
  } else {
    navigationItems[2].secondaryIcon = null;
  }

  const feesItem = NAVIGATION_LINK_MENU.navigatorLinkItems[4];
  feesItem.to = `${getConfig().REACT_APP_BASE_PATH}/fees`;

  return (
    <Routes>
      <Route
        path="/"
        element={
          <Layout
            clientPortalIdentity={clientPortalIdentity}
            navigationItems={navigationItems}
          />
        }>
        <Route
          path="/"
          element={
            <Navigate state={{ from: '/' }} to="/accounts/all" replace />
          }
        />
        <Route
          path="/accounts"
          element={
            <Navigate
              state={{ from: '/accounts' }}
              to="/accounts/all"
              replace
            />
          }
        />
        <Route
          path="/asset_valuation"
          element={
            <Navigate
              state={{ from: '/asset_valuation' }}
              to={`/accounts/${clientPortalIdentity}/asset_valuation`}
            />
          }
        />
        <Route path="/fees" element={<FeesRedirect />} />
        <Route path="/accounts/all/*" element={<Accounts />} />
        <Route path="/accounts/:accountNumber/*" element={<Account />} />
        <Route
          path="/profile/*"
          element={<Profile setProfileSecurityQuestion={setQuestion} />}
        />
        <Route path="/documents/*" element={<Documents />} />
        <Route path="/funding/*" element={<Funding />} />

        <Route path="*" element={<Navigate to="/" />} />
      </Route>
    </Routes>
  );
};

export default Individual;
